import { FC, useCallback, useContext, useEffect, useState } from "react";
import "./TaskInformationForm.css";
import { useParams } from "react-router-dom";
import { Context } from "../../../index";
import TaskAddEditForm from "../TaskAddEditForm/TaskAddEditForm";
import { IFunctionPermissions } from "../../../common/api_interfaces/role/IFunctionPermissions";
import { AppContext } from "../../../App";
import CopyTextButton from "../../buttons/CopyTextButton/CopyTextButton";
import QuillPreview from "../../Shared/TextEditor/QuillPreview/QuillPreview";
import { getOptionsQueryParams, IOptionsQueryParams } from "../../../helpers/paramsAutoFiller";
import { TaskContext } from "../../../contexts/TaskContext";
import { handleError } from "../../../helpers/ErrorLib/handleError";
import Button from "../../UiLib/Button/Button";

import { ReactComponent as BluePen } from "../../../assets/editButtonGray.svg"

interface ITaskPage {
    projectId?: number;
    userAccess: IFunctionPermissions[];
    taskNumber?: string;
    optionsQueryParams: IOptionsQueryParams;
}

const TaskInformationForm: FC<ITaskPage> = ({ projectId, taskNumber, optionsQueryParams }) => {
    const { showToast } = useContext(AppContext);
    const taskContext = useContext(TaskContext);
    const { id } = useParams();
    const [showEditForm, setShowEditForm] = useState<boolean>(false);
    const { store } = useContext(Context);

    useEffect(() => {
        const options = getOptionsQueryParams(window.location.search);
        if (options.page === "task" && options.entity === "task-form" && options.action === "edit") {
            setShowEditForm(true);
        }
    }, []);

    const onEditTaskClick = useCallback(async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();

        if (!taskContext.editTask) {
            showToast("У вас нет прав для редактирования задачи!");
            return;
        }

        try {
            const response = await store.taskService.getTaskForm(Number(id));

            if (response.data?.id) {
                setShowEditForm(prev => !prev);
                await taskContext.loadTaskInfo();
            }
        } catch (error) {
            setShowEditForm(prev => !prev);
            handleError(error, {
                defaultMessage: "Неизвестная ошибка при открытии формы редактирования",
                showToast
            });
        }
    }, [taskContext.editTask, id, store.taskService, taskContext.loadTaskInfo, showToast]);

    const onEditFormClose = useCallback(async () => {
        setShowEditForm(false);

        try {
            await Promise.all([
                taskContext.loadTaskInfo(),
                taskContext.getAllTaskPerson()
            ]);
        } catch (error) {
            handleError(error, {
                defaultMessage: "Неизвестная ошибка при закрытии формы редактирования",
                showToast
            });
        }
    }, [taskContext.loadTaskInfo, taskContext.getAllTaskPerson, showToast]);

    const taskInfo = taskContext.taskInformation;
    const hasEditPermission = taskContext.editTask;

    return (
        <div className="widget__task--information">
            {showEditForm && (
                <div className="full-screen-overlay">
                    <div className="popup-form-background">
                        <TaskAddEditForm
                            responsibleRequired={taskContext.projectSettings.responsibleRequired}
                            mode="edit"
                            taskId={Number(id)}
                            closeFunc={onEditFormClose}
                            projectId={projectId}
                            permission={hasEditPermission}
                            optionsQueryParams={{
                                page: optionsQueryParams.page,
                                entity: "task-form",
                                action: "edit",
                                entityId: Number(id),
                            }}
                        />
                    </div>
                </div>
            )}

            <div className="widget__task--information__head">
                <div className="widget__task--information__name">
                    <div className="widget__task--information__id">
                        <div>
                            <p className="widget__task--information__p--style-abr" id={taskNumber}>
                                {taskNumber}
                            </p>
                        </div>
                        <div>
                            <CopyTextButton
                                title="Скопировать номер задачи"
                                titlePosition="right"
                                textDivId={taskNumber ?? ""}
                                text={ taskNumber &&taskInfo?.shortDescription ? `${taskNumber} ${taskInfo?.shortDescription}`: undefined}
                            />
                        </div>
                    </div>
                    <div className="widget-task-information-desk">
                        <p className="widget__task--information__p--style">
                            {taskInfo?.shortDescription}
                        </p>
                    </div>
                </div>
                <div>
                    <Button
                        id={hasEditPermission ? "button-pen" : "button-pen-gray"}
                        callBack={(e) => {onEditTaskClick(e);}}
                        image={<BluePen />}
                    />
                </div>
            </div>

            <div className="widget__task--information__desk--block">
                <QuillPreview htmlContent={taskInfo?.description ?? ""} />
            </div>
        </div>
    );
};

export default TaskInformationForm;